/** The evidence chain behind a figure, as six links in order (`inventory/evidence_status.py`):
 * referenced, stored, retrievable, verified, priced, ledgered.
 *
 * A register row carries two facts about it and no more: whether the receipt is complete, and - when
 * it is not - **the first link that breaks** (`evidence_broken_at`). So the honest drawing is three
 * kinds of link, not two: *met* (before the break, or all of them when complete), *broken* (the one
 * the server named) and **not checked** (after the break: the server stops at the first failure, so
 * it has said nothing about the rest, and drawing them met would be a claim, and drawing them broken
 * would be another). A broken step the server names that this list does not know leaves every link
 * not checked and says so, rather than guessing which one it meant.
 */

export const CHAIN_LINKS = [
  { key: "referenced", label: "Cited" },
  { key: "stored", label: "Stored" },
  { key: "retrievable", label: "Retrievable" },
  { key: "verified", label: "Verified" },
  { key: "priced", label: "Priced" },
  { key: "ledgered", label: "In the ledger" },
] as const;

export type LinkStatus = "met" | "broken" | "unchecked";

export interface ChainLink {
  key: string;
  label: string;
  status: LinkStatus;
}

export function chainOf(figure: {
  receipt_is_complete: boolean;
  evidence_broken_at?: string | null;
}): ChainLink[] {
  if (figure.receipt_is_complete) {
    return CHAIN_LINKS.map(({ key, label }) => ({ key, label, status: "met" as const }));
  }
  const at = CHAIN_LINKS.findIndex((link) => link.key === figure.evidence_broken_at);
  return CHAIN_LINKS.map(({ key, label }, index) => ({
    key,
    label,
    status: at < 0 ? "unchecked" : index < at ? "met" : index === at ? "broken" : "unchecked",
  }));
}

/** The one-line reading of a chain, for the accessible name and the tooltip. */
export function chainSentence(links: readonly ChainLink[]): string {
  const met = links.filter((link) => link.status === "met").length;
  const broken = links.find((link) => link.status === "broken");
  if (broken) return `Evidence chain breaks at ${broken.label.toLowerCase()}: ${met} of ${links.length} links met`;
  if (met === links.length) return `Evidence chain complete: ${met} of ${links.length} links met`;
  return `Evidence chain not fully checked: ${met} of ${links.length} links met`;
}
